import { useEffect, useRef } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import axiosInstance from "@/lib/axios";
import { connectedToolsEndpoint as base } from "@/config/endpoints";
import { refetchRoutes } from "@/hooks/api/useRouterData";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import type {
  ActiveTool,
  LeadTool,
  RunActionResult,
  ToolDescribedField,
  ToolDetail,
  ToolEndpoint,
  ToolEventRow,
  ToolFilters,
  ToolManifest,
  ToolOverview,
  ToolRecordPage,
  ToolSettings,
  ToolSurfacesView,
  ToolTryResult,
  ToolsList,
  ValidateResult,
} from "../types";

const KEY = "connected-tools";

const unwrap = <T,>(res: { data: { data: T } }) => res.data.data;

export const toolErrorMessage = (error: unknown, fallback = "Something went wrong") =>
  isAxiosError(error) ? (error.response?.data?.message ?? fallback) : fallback;

const showError = (error: unknown) => {
  toast.error(toolErrorMessage(error));
};

const cleanFilters = (filters: ToolFilters & { page?: number; pageSize?: number }) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));

export function useConnectedTools() {
  return useQuery({
    queryKey: [KEY, "list"],
    queryFn: async () => unwrap<ToolsList>(await axiosInstance.get(base)),
  });
}

/** Decides whether a lead shows its Tools tab. Rarely changes, asked on every drawer. */
export function useActiveConnectedTools() {
  return useQuery({
    queryKey: [KEY, "active"],
    queryFn: async () =>
      unwrap<{ tools: ActiveTool[] }>(await axiosInstance.get(`${base}/active`)),
    staleTime: 5 * 60 * 1000,
  });
}

export function useToolDetail(id: number | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    enabled: id != null,
    queryFn: async () => unwrap<ToolDetail>(await axiosInstance.get(`${base}/${id}`)),
  });
}

export function useLeadTools(leadId: number | string | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [KEY, "lead", String(leadId)],
    enabled: !!leadId && enabled,
    // A student takes the test while the counsellor watches: results walk in.
    refetchInterval: 30_000,
    queryFn: async () => unwrap<{ tools: LeadTool[] }>(await axiosInstance.get(`${base}/lead/${leadId}`)),
  });

  const statuses = query.data?.tools.map((t) => t.records[0]?.status.value ?? "").join("|");
  const seen = useRef(statuses);
  useEffect(() => {
    if (statuses === undefined) return;
    if (seen.current !== undefined && seen.current !== statuses) refreshLead(queryClient);
    seen.current = statuses;
  }, [statuses, queryClient]);

  return query;
}

/** A tool's status can move the lead: the open drawer and the lists follow. */
function refreshLead(queryClient: ReturnType<typeof useQueryClient>) {
  refetchCurrentLead();
  queryClient.invalidateQueries({ queryKey: ["leads"] });
}

export function useRunToolAction(leadId: number | string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { toolId: number; actionKey: string; input?: Record<string, unknown> }) =>
      unwrap<RunActionResult>(
        await axiosInstance.post(`${base}/lead/${leadId}/${vars.toolId}/actions/${vars.actionKey}`, {
          input: vars.input ?? {},
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData([KEY, "lead", String(leadId)], result.lead);
      refreshLead(queryClient);
      if (result.warning) toast.warning(result.warning);
      if (result.problem) toast.warning(result.problem);
      if (result.empty) toast.info("Nothing there yet");
    },
    onError: showError,
  });
}

export function useToolOverview(id: number | null, filters: ToolFilters) {
  return useQuery({
    queryKey: [KEY, "overview", id, filters],
    enabled: id != null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap<ToolOverview>(await axiosInstance.get(`${base}/${id}/overview`, { params: cleanFilters(filters) })),
  });
}

export function useToolRecords(id: number | null, filters: ToolFilters & { page: number }) {
  return useQuery({
    queryKey: [KEY, "records", id, filters],
    enabled: id != null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap<ToolRecordPage>(
        await axiosInstance.get(`${base}/${id}/records`, { params: cleanFilters({ ...filters, pageSize: 25 }) }),
      ),
  });
}

export function useToolEvents(id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "events", id],
    enabled: id != null && enabled,
    refetchInterval: enabled ? 15_000 : false,
    queryFn: async () =>
      unwrap<{ total: number; items: ToolEventRow[] }>(await axiosInstance.get(`${base}/${id}/events`)),
  });
}

/** Every path this tool has really sent, from stored results and webhooks. */
export function useToolSamples(id: number | null) {
  return useQuery({
    queryKey: [KEY, "samples", id],
    enabled: id != null,
    queryFn: async () =>
      unwrap<{ fields: ToolDescribedField[]; payloads: number; sample: unknown }>(
        await axiosInstance.get(`${base}/${id}/samples`),
      ),
  });
}

export function useDescribeSample() {
  return useMutation({
    mutationFn: async (sample: unknown) =>
      unwrap<{ fields: ToolDescribedField[] }>(await axiosInstance.post(`${base}/describe`, { sample })),
    onError: showError,
  });
}

export function useToolPreview(manifest: ToolManifest, data: unknown) {
  return useQuery({
    queryKey: [KEY, "preview", manifest, data],
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap<{ valid: boolean; errors: string[]; surfaces: ToolSurfacesView | null }>(
        await axiosInstance.post(`${base}/preview`, { manifest, data }),
      ),
  });
}

export const loadToolLeadFields = async (leadId: number) =>
  unwrap<{ fields: { path: string; sample: string; isList?: boolean; itemPaths?: { path: string; sample: string }[] }[] }>(
    await axiosInstance.get(`${base}/lead/${leadId}/fields`),
  ).fields;

export function useTryEndpoint(toolId: number) {
  return useMutation({
    mutationFn: async (vars: { leadId: number; action: ToolEndpoint; input?: Record<string, unknown> }) =>
      unwrap<ToolTryResult>(
        await axiosInstance.post(`${base}/${toolId}/try/${vars.leadId}`, { action: vars.action, input: vars.input ?? {} }),
      ),
    onError: showError,
  });
}

export function useValidateManifest() {
  return useMutation({
    mutationFn: async (manifest: unknown) =>
      unwrap<ValidateResult>(await axiosInstance.post(`${base}/validate`, { manifest })),
    onError: showError,
  });
}

export function useToolAdmin() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: [KEY] });
  /** The Tools tab in the sidebar and on a lead appear and go with the connection. */
  const refreshRoutes = () => {
    refresh();
    refetchRoutes();
  };

  const create = useMutation({
    mutationFn: async (manifest: unknown) =>
      unwrap<ToolDetail>(await axiosInstance.post(base, { manifest })),
    onSuccess: refresh,
    onError: showError,
  });
  const updateManifest = useMutation({
    mutationFn: async (vars: { id: number; manifest: unknown }) =>
      unwrap<ToolDetail>(await axiosInstance.put(`${base}/${vars.id}/manifest`, { manifest: vars.manifest })),
    onSuccess: (tool) => {
      refreshRoutes();
      if (tool.isActive) toast.success("Definition saved");
      // The server drops the keys when the definition points at a new host.
      else toast.success("Definition saved. Enter the keys for the new host and connect again.");
    },
    onError: showError,
  });
  const saveCredentials = useMutation({
    mutationFn: async (vars: { id: number; values: Record<string, string> }) =>
      unwrap<ToolDetail>(await axiosInstance.put(`${base}/${vars.id}/credentials`, { values: vars.values })),
    onSuccess: () => {
      refresh();
      toast.success("Credentials saved");
    },
    onError: showError,
  });
  const saveSettings = useMutation({
    mutationFn: async (vars: { id: number; settings: ToolSettings }) =>
      unwrap<ToolDetail>(await axiosInstance.put(`${base}/${vars.id}/settings`, { settings: vars.settings })),
    onSuccess: () => {
      refresh();
      toast.success("Saved");
    },
    onError: showError,
  });
  const test = useMutation({
    mutationFn: async (id: number) =>
      unwrap<{ ok: boolean; statusCode: number | null; message: string }>(
        await axiosInstance.post(`${base}/${id}/test`),
      ),
    onSuccess: (result) => (result.ok ? toast.success(result.message) : toast.error(result.message)),
    onError: showError,
  });
  const connect = useMutation({
    mutationFn: async (id: number) => unwrap<ToolDetail>(await axiosInstance.post(`${base}/${id}/connect`)),
    onSuccess: (tool) => {
      refreshRoutes();
      toast.success(`${tool.name} connected`);
    },
    onError: showError,
  });
  const disconnect = useMutation({
    mutationFn: async (id: number) => unwrap<ToolDetail>(await axiosInstance.post(`${base}/${id}/disconnect`)),
    onSuccess: (tool) => {
      refreshRoutes();
      toast.success(`${tool.name} disconnected`);
    },
    onError: showError,
  });
  const rotateWebhook = useMutation({
    mutationFn: async (id: number) =>
      unwrap<ToolDetail>(await axiosInstance.post(`${base}/${id}/rotate-webhook`)),
    onSuccess: () => {
      refresh();
      toast.success("New webhook URL made. Give it to the tool.");
    },
    onError: showError,
  });
  const remove = useMutation({
    mutationFn: async (id: number) => unwrap(await axiosInstance.delete(`${base}/${id}`)),
    onSuccess: () => {
      refreshRoutes();
      toast.success("Tool removed");
    },
    onError: showError,
  });

  return { create, updateManifest, saveCredentials, saveSettings, test, connect, disconnect, rotateWebhook, remove };
}
